<template>
  <div class="manual-allocation-dashboard">
    <navbar :hasSearch="false" />
    <div id="content">

    <!-- Breadcrumb -->
    <div class="row mb-3">
      <div class="col-12">
        <div class="card h-100">
          <ol class="breadcrumb my-auto">
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'landingPage' }"><a class="black-text" href="#">Home</a></router-link>
              <i class="fa fa-angle-right mx-2" aria-hidden="true"></i>
            </li>
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'dashboard', params: { customerRef: customerId } }"><a class="black-text" href="#">Main Dashboard</a></router-link>
              <i class="fa fa-angle-right mx-2" aria-hidden="true"></i>
            </li>
            <li class="breadcrumb-item active font-weight-bold">
              <a class="black-text" href="#">Manual Allocations</a>
            </li>
          </ol>
        </div>
      </div>
    </div>

    <!-- Blocking error: fatalError from fetchDashboardViewModel non-2xx/non-404 -->
    <b-alert v-if="fatalError" variant="danger" show class="fatal-error-alert">
      <h5>Dashboard Unavailable</h5>
      <p>{{ fatalError }}</p>
    </b-alert>

    <!-- Main dashboard content -->
    <div v-else class="dashboard-content">
      <!-- Account info panel -->
      <div v-if="accountInfo" class="account-info-panel card w-100 p-3 mb-3">
        <div class="row">
          <div class="col-md-3">
            <div class="account-info-title text-muted mb-1">Customer Reference</div>
            <div class="account-info-value">
              <i class="fas fa-address-card text-muted mr-2" style="font-size:0.95rem;"></i>{{ accountInfo.CustomerReference }}
            </div>
          </div>
          <div class="col-md-3">
            <div class="account-info-title text-muted mb-1">Customer's Name</div>
            <div class="account-info-value">
              <i class="fas fa-building text-muted mr-2" style="font-size:0.95rem;"></i>{{ accountInfo.CustomerName }}
            </div>
          </div>
          <div class="col-md-6">
            <div class="account-info-title text-muted mb-1">Primary Contact</div>
            <div v-if="accountInfo.PrimaryContact" class="primary-contact">
              <div class="account-info-value contact-name">
                <i class="fas fa-user text-muted mr-2" style="font-size:0.95rem;"></i>{{ accountInfo.PrimaryContact.FirstName }} {{ accountInfo.PrimaryContact.LastName }}
              </div>
              <div class="text-muted account-contact-details">
                <span v-if="accountInfo.PrimaryContact.Email" class="contact-email">{{ accountInfo.PrimaryContact.Email }}</span>
                <span v-if="accountInfo.PrimaryContact.Landline" class="contact-landline ml-2">• {{ accountInfo.PrimaryContact.Landline }}</span>
                <span v-if="accountInfo.PrimaryContact.Mobile" class="contact-mobile ml-2">• {{ accountInfo.PrimaryContact.Mobile }}</span>
              </div>
            </div>
            <div v-else class="no-primary-contact text-muted">No primary contact</div>
          </div>
        </div>
      </div>

      <!-- Transaction summary tiles -->
      <div v-if="transactionSummary" class="summary-tiles row mt-3 mb-3">
        <div class="col-md-3">
          <div class="tile card p-3">
            <div class="d-flex justify-content-between align-items-start">
              <div class="tile-label text-muted" style="font-size:1.1rem;">Total Transactions</div>
              <div class="tile-icon-box" style="width:40px;height:40px;border-radius:8px;background:rgba(47,111,228,0.15);flex-shrink:0;display:flex;align-items:center;justify-content:center;">
                <i class="fas fa-layer-group" style="color:#2f6fe4;font-size:1.1rem;"></i>
              </div>
            </div>
            <div class="tile-count font-weight-bold mt-1" style="font-size:1.8rem;line-height:1.1;">{{ transactionSummary.Total.Count }}</div>
            <div class="tile-amount mt-1" style="color:#7267EF;">{{ transactionSummary.Total.Amount | currency }}</div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="tile card p-3">
            <div class="d-flex justify-content-between align-items-start">
              <div class="tile-label text-muted" style="font-size:1.1rem;">Fully Allocated Transactions</div>
              <div class="tile-icon-box" style="width:40px;height:40px;border-radius:8px;background:rgba(47,111,228,0.15);flex-shrink:0;display:flex;align-items:center;justify-content:center;">
                <i class="far fa-check-circle" style="color:#2f6fe4;font-size:1.1rem;"></i>
              </div>
            </div>
            <div class="tile-count font-weight-bold mt-1" style="font-size:1.8rem;line-height:1.1;">{{ transactionSummary.Allocated.Count }}</div>
            <div class="tile-amount mt-1 summary-amount-highlight" style="color:#28a745;">{{ transactionSummary.Allocated.Amount | currency }}</div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="tile card p-3">
            <div class="d-flex justify-content-between align-items-start">
              <div class="tile-label text-muted" style="font-size:1.1rem;">Partially Allocated Transactions</div>
              <div class="tile-icon-box" style="width:40px;height:40px;border-radius:8px;background:rgba(47,111,228,0.15);flex-shrink:0;display:flex;align-items:center;justify-content:center;">
                <i class="fas fa-chart-pie" style="color:#2f6fe4;font-size:1.1rem;"></i>
              </div>
            </div>
            <div class="tile-count font-weight-bold mt-1" style="font-size:1.8rem;line-height:1.1;">{{ transactionSummary.PartiallyAllocated.Count }}</div>
            <div class="tile-amount mt-1 summary-amount-highlight" style="color:#e0a800;">{{ transactionSummary.PartiallyAllocated.Amount | currency }}</div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="tile card p-3">
            <div class="d-flex justify-content-between align-items-start">
              <div class="tile-label text-muted" style="font-size:1.1rem;">Unallocated Transactions</div>
              <div class="tile-icon-box" style="width:40px;height:40px;border-radius:8px;background:rgba(47,111,228,0.15);flex-shrink:0;display:flex;align-items:center;justify-content:center;">
                <i class="far fa-circle" style="color:#2f6fe4;font-size:1.1rem;"></i>
              </div>
            </div>
            <div class="tile-count font-weight-bold mt-1" style="font-size:1.8rem;line-height:1.1;">{{ transactionSummary.Unallocated.Count }}</div>
            <div class="tile-amount text-muted mt-1">{{ transactionSummary.Unallocated.Amount | currency }}</div>
          </div>
        </div>
      </div>

      <!-- Loading indicator -->
      <b-spinner v-if="loading" label="Loading..." class="loading-spinner" />

      <!-- Tabs -->
      <b-tabs class="dashboard-tabs">
        <b-tab title="Transactions" active>
          <!-- Grouped unit: Filter + Pagination + Table -->
          <div class="card">

          <!-- Filter panel -->
          <div class="filter-panel p-3 border-bottom">
            <!-- Row 1: Search, Date, Amount range, Balance range -->
            <b-row align-v="end" class="mb-3">
              <b-col md="2">
                <b-form-group label="Reference" class="mb-0" label-class="filter-label">
                  <b-form-input
                    v-model="referenceFilter"
                    placeholder="Search reference..."
                    @input="handleReferenceFilterChange"
                  />
                </b-form-group>
              </b-col>
              <b-col md="3">
                <b-form-group label="Date Range" class="mb-0" label-class="filter-label">
                  <el-date-picker
                    v-model="dateRange"
                    type="daterange"
                    value-format="yyyy-MM-dd"
                    range-separator="-"
                    start-placeholder="From"
                    end-placeholder="To"
                    style="width: 100%"
                    @change="handleDateRangeChange"
                  />
                </b-form-group>
              </b-col>
              <b-col md="3">
                <b-form-group label="Amount Range" class="mb-0" label-class="filter-label">
                  <b-input-group>
                    <b-form-input
                      v-model.number="amountFrom"
                      type="number"
                      placeholder="Min"
                      class="mr-1"
                      @change="handleAmountChange"
                    />
                    <b-form-input
                      v-model.number="amountTo"
                      type="number"
                      placeholder="Max"
                      @change="handleAmountChange"
                    />
                  </b-input-group>
                </b-form-group>
              </b-col>
              <b-col md="3">
                <b-form-group label="Balance Range" class="mb-0" label-class="filter-label">
                  <b-input-group>
                    <b-form-input
                      v-model.number="balanceFrom"
                      type="number"
                      placeholder="Min"
                      class="mr-1"
                      @change="handleBalanceChange"
                    />
                    <b-form-input
                      v-model.number="balanceTo"
                      type="number"
                      placeholder="Max"
                      @change="handleBalanceChange"
                    />
                  </b-input-group>
                </b-form-group>
              </b-col>
            </b-row>
            <!-- Row 2: Status, Type, Action buttons -->
            <b-row align-v="end">
              <b-col md="4">
                <b-form-group label="Status" class="mb-0" label-class="filter-label">
                  <multiselect
                    v-model="selectedStatuses"
                    :options="statusOptions"
                    :multiple="true"
                    :close-on-select="false"
                    :clear-on-select="false"
                    :preserve-search="true"
                    :searchable="false"
                    label="text"
                    track-by="value"
                    placeholder="Select statuses..."
                    @input="handleStatusFilterChange"
                  />
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="Type" class="mb-0" label-class="filter-label">
                  <multiselect
                    v-model="selectedTypes"
                    :options="typeOptions"
                    :multiple="true"
                    :close-on-select="false"
                    :clear-on-select="false"
                    :preserve-search="true"
                    :searchable="false"
                    label="text"
                    track-by="value"
                    placeholder="Select types..."
                    @input="handleTypeFilterChange"
                  />
                </b-form-group>
              </b-col>
              <b-col cols="auto" class="ml-auto">
                <b-button variant="primary" class="mr-2 dashboard-filter-button" @click="handleApplyFilters">Apply Filters</b-button>
                <b-button variant="secondary" class="dashboard-filter-button" @click="handleResetFilters">Clear Filters</b-button>
              </b-col>
            </b-row>
          </div>

          <!-- Pagination + Table section -->
          <div class="p-3">

            <!-- Inline error (filter validation / fetchTransactions failures) -->
            <b-alert v-if="error" variant="danger" show class="mb-2">{{ error }}</b-alert>

            <!-- Pagination -->
            <div v-if="pagination.TotalItems > 0" class="pagination-controls mb-2">
              <b-row align-v="center">
                <b-col cols="auto">
                  <span class="text-muted small">{{ showingText }}</span>
                </b-col>
                <b-col class="d-flex justify-content-center">
                  <b-pagination
                    :value="pagination.Page"
                    :total-rows="pagination.TotalItems"
                    :per-page="pagination.PageSize"
                    :limit="7"
                    size="sm"
                    class="mb-0"
                    @change="goToPage"
                  >
                    <template v-slot:first-text><i class="fas fa-angle-double-left"></i></template>
                    <template v-slot:prev-text><i class="fas fa-angle-left"></i></template>
                    <template v-slot:next-text><i class="fas fa-angle-right"></i></template>
                    <template v-slot:last-text><i class="fas fa-angle-double-right"></i></template>
                  </b-pagination>
                </b-col>
                <b-col cols="auto">
                  <b-form-select
                    :value="query.pageSize"
                    :options="pageSizeOptions"
                    size="sm"
                    @change="changePageSize"
                  />
                </b-col>
              </b-row>
            </div>

            <!-- Schema table -->
            <schema-table
              :schema="transactionSchema"
              :data="allocations"
              :loading="loading"
              :data-loading-error="error"
              :on-retry="retryFetch"
              :empty-text="emptyText"
              @sort-changed="handleSortChanged"
              @action-click="handleActionClick"
            />

          </div><!-- end pagination + table section -->
          </div><!-- end grouped card -->
        </b-tab>

        <b-tab title="History">
          <p class="mt-3">Transaction history coming soon.</p>
        </b-tab>
      </b-tabs>
    </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import filtersMixin from '@/mixins/filters'
import Navbar from '@/components/Navbar.vue'
import SchemaTable from '@/components/common/SchemaTable.vue'

export default {
  name: 'ManualAllocationDashboard',

  components: {
    Navbar,
    SchemaTable
  },

  mixins: [filtersMixin],

  beforeRouteUpdate: function (to, from, next) {
    var vm = this
    this.initializeDashboard(to.params.customerId)
      .then(function () { next() })
      .catch(function () { next() })
  },

  data: function () {
    return {
      referenceFilter: '',
      dateRange: null,
      amountFrom: null,
      amountTo: null,
      balanceFrom: null,
      balanceTo: null,
      selectedStatuses: [
        { text: 'Unallocated', value: 'Unallocated' },
        { text: 'Partially Allocated', value: 'PartiallyAllocated' }
      ],
      selectedTypes: [],
      statusOptions: [
        { text: 'Unallocated', value: 'Unallocated' },
        { text: 'Partially Allocated', value: 'PartiallyAllocated' },
        { text: 'Allocated', value: 'Allocated' }
      ],
      typeOptions: [
        { text: 'Invoices', value: 'Invoice' },
        { text: 'Credit Notes', value: 'CreditNote' },
        { text: 'Write Offs', value: 'WriteOff' },
        { text: 'Write Backs', value: 'WriteBack' },
        { text: 'Refunds', value: 'Refund' },
        { text: 'Payments', value: 'Payment' }
      ],
      pageSizeOptions: [
        { text: '10 per page', value: 10 },
        { text: '50 per page', value: 50 },
        { text: '100 per page', value: 100 },
        { text: '200 per page', value: 200 },
        { text: '500 per page', value: 500 }
      ]
    }
  },

  computed: Object.assign(
    mapState({
      accountInfo: function (state) { return state.allocations.accountInfo },
      transactionSummary: function (state) { return state.allocations.transactionSummary },
      allocations: function (state) { return state.allocations.allocations },
      query: function (state) { return state.allocations.query },
      pagination: function (state) { return state.allocations.pagination },
      loading: function (state) { return state.allocations.loading },
      error: function (state) { return state.allocations.error },
      fatalError: function (state) { return state.allocations.fatalError }
    }),
    {
      customerId: function () {
        return this.$route.params.customerId
      },

      hasActiveFilters: function () {
        var filters = this.query.filters
        if (!filters || filters.length === 0) return false
        // Has active filters if there's more than just the default status FilterItem
        var nonStatusFilters = filters.filter(function (f) { return f.field !== 'status' })
        if (nonStatusFilters.length > 0) return true
        // Check if status filter has non-default values
        var statusFilter = filters.find(function (f) { return f.field === 'status' })
        if (!statusFilter) return false
        var defaultStatuses = ['Unallocated', 'PartiallyAllocated']
        if (statusFilter.values.length !== defaultStatuses.length) return true
        return !defaultStatuses.every(function (s) { return statusFilter.values.indexOf(s) >= 0 })
      },

      emptyText: function () {
        if (this.hasActiveFilters) {
          return 'No matching transactions \u2014 try adjusting your filters'
        }
        return 'No allocatable transactions found for this account'
      },

      allStatusesChecked: function () {
        return this.selectedStatuses.length === 3
      },

      transactionSchema: function () {
        return {
          columns: [
            { key: 'Reference', label: 'Reference', sortable: true, width: '180px', component: 'ReferenceCell' },
            { key: 'Date', label: 'Date', sortable: true, width: '110px', formatter: 'shortDate' },
            { key: 'Amount', label: 'Amount', sortable: true, width: '120px', formatter: 'currency' },
            { key: 'RemainingBalance', label: 'Remaining Balance', sortable: true, width: '160px', formatter: 'currency' },
            {
              key: 'Status',
              label: 'Status',
              sortable: true,
              width: '160px',
              component: 'StatusCell'
            },
            { key: 'Type', label: 'Type', sortable: true, width: '130px' }
          ],
          actions: [
            {
              key: 'allocate-new',
              label: 'Allocate New',
              icon: 'fas fa-plus',
              hideLabel: true,
              class: 'btn btn-link text-secondary p-1 mr-2 d-inline-flex align-items-center',
              disabled: function (item) { return !item.CanAllocateNew }
            },
            {
              key: 'edit-allocations',
              label: 'Edit Allocations',
              icon: 'fas fa-pencil-alt',
              hideLabel: true,
              class: 'btn btn-link text-secondary p-1 d-inline-flex align-items-center',
              disabled: function (item) { return !item.CanEditAllocations }
            }
          ],
          options: {
            pagination: false
          }
        }
      },

      pageInfo: function () {
        return 'Page ' + this.pagination.Page + ' of ' + this.pagination.TotalPages
      },

      itemRange: function () {
        var first = ((this.pagination.Page - 1) * this.pagination.PageSize) + 1
        var last = Math.min(this.pagination.Page * this.pagination.PageSize, this.pagination.TotalItems)
        return 'Items ' + first + ' to ' + last + ' of ' + this.pagination.TotalItems
      },

      showingText: function () {
        var first = ((this.pagination.Page - 1) * this.pagination.PageSize) + 1
        var last = Math.min(this.pagination.Page * this.pagination.PageSize, this.pagination.TotalItems)
        return 'Showing ' + first + ' to ' + last + ' of ' + this.pagination.TotalItems + ' entries'
      }
    }
  ),

  created: function () {
    this.initializeDashboard(this.customerId)
  },

  methods: {
    initializeDashboard: function (customerId) {
      var vm = this
      return Promise.resolve(this.$store.dispatch('initializeFiltersForCustomer', customerId))
        .then(function () {
          vm.syncFilterControlsFromQuery()
          return Promise.all([
            vm.$store.dispatch('fetchDashboardViewModel', customerId),
            vm.$store.dispatch('fetchTransactions', customerId)
          ])
        })
    },

    syncFilterControlsFromQuery: function () {
      var filters = (this.query && this.query.filters) || []
      var getFilter = function (field, operator) {
        return filters.find(function (f) {
          return f.field === field && (!operator || f.operator === operator)
        })
      }

      var referenceFilter = getFilter('reference', 'contains')
      this.referenceFilter = referenceFilter && referenceFilter.values && referenceFilter.values.length
        ? referenceFilter.values[0]
        : ''

      var dateFromFilter = getFilter('date', 'gte')
      var dateToFilter = getFilter('date', 'lte')
      if (dateFromFilter && dateToFilter && dateFromFilter.values.length && dateToFilter.values.length) {
        this.dateRange = [dateFromFilter.values[0], dateToFilter.values[0]]
      } else {
        this.dateRange = null
      }

      var amountFromFilter = getFilter('amount', 'gte')
      var amountToFilter = getFilter('amount', 'lte')
      this.amountFrom = amountFromFilter && amountFromFilter.values.length ? amountFromFilter.values[0] : null
      this.amountTo = amountToFilter && amountToFilter.values.length ? amountToFilter.values[0] : null

      var balanceFromFilter = getFilter('remainingBalance', 'gte')
      var balanceToFilter = getFilter('remainingBalance', 'lte')
      this.balanceFrom = balanceFromFilter && balanceFromFilter.values.length ? balanceFromFilter.values[0] : null
      this.balanceTo = balanceToFilter && balanceToFilter.values.length ? balanceToFilter.values[0] : null

      var statusFilter = getFilter('status', 'in')
      var statusValues = statusFilter && Array.isArray(statusFilter.values)
        ? statusFilter.values
        : ['Unallocated', 'PartiallyAllocated']
      this.selectedStatuses = statusValues.map(function (value) {
        var fromOption = this.statusOptions.find(function (option) { return option.value === value })
        return fromOption || { text: value, value: value }
      }, this)

      var typeFilter = getFilter('type', 'in')
      var typeValues = typeFilter && Array.isArray(typeFilter.values) ? typeFilter.values : []
      this.selectedTypes = typeValues.map(function (value) {
        var fromOption = this.typeOptions.find(function (option) { return option.value === value })
        return fromOption || { text: value, value: value }
      }, this)
    },

    retryFetch: function () {
      this.$store.dispatch('fetchTransactions', this.customerId)
    },

    handleSortChanged: function (context) {
      // Column keys are PascalCase (Amendment A001), but the request body (state.query)
      // remains camelCase. Lowercase the first character of sortBy before committing.
      var sortBy = context.sortBy || ''
      if (sortBy.length > 0) {
        sortBy = sortBy.charAt(0).toLowerCase() + sortBy.slice(1)
      }
      this.$store.commit('setQuery', {
        sortBy: sortBy,
        sortDirection: context.sortDesc ? 'desc' : 'asc',
        page: 1
      })
      this.$store.dispatch('fetchTransactions', this.customerId)
    },

    handleActionClick: function (payload) {
      var action = payload.action
      var item = payload.item
      if (action === 'allocate-new') {
        var transactionType = this.resolveTransactionType(item)
        this.$router.push({
          name: 'manualAllocationsView',
          params: { transactionId: item.Id, transactionType: transactionType }
        })
      } else if (action === 'edit-allocations') {
        var editTransactionType = this.resolveTransactionType(item)
        this.$router.push({
          name: 'manualAllocationsEdit',
          params: {
            customerReference: this.customerId,
            transactionId: item.Id,
            transactionType: editTransactionType
          }
        })
      }
      // unknown action: do nothing
    },

    resolveTransactionType: function (item) {
      var bankOrSales = item && item.BankOrSales
      if (bankOrSales === 'Bank' || bankOrSales === 'Sales') {
        return bankOrSales
      }
      var bankTypes = ['Payment', 'Refund']
      if (item && bankTypes.indexOf(item.Type) >= 0) {
        return 'Bank'
      }
      return 'Sales'
    },

    handleReferenceFilterChange: function (value) {
      this.$store.commit('setAllocationsFilter', { field: 'reference', value: value })
    },

    handleDateRangeChange: function (dates) {
      this.$store.commit('setAllocationsFilter', { field: 'date', value: dates })
    },

    handleAmountChange: function () {
      this.$store.commit('setAllocationsFilter', {
        field: 'amount',
        value: { from: this.amountFrom, to: this.amountTo }
      })
    },

    handleBalanceChange: function () {
      this.$store.commit('setAllocationsFilter', {
        field: 'remainingBalance',
        value: { from: this.balanceFrom, to: this.balanceTo }
      })
    },

    handleStatusFilterChange: function (selectedOptions) {
      var values = selectedOptions.map(function (o) { return o.value })
      this.$store.commit('setAllocationsFilter', { field: 'status', value: values })
    },

    handleSelectAllStatuses: function (checked) {
      var allStatuses = checked ? [
        { text: 'Unallocated', value: 'Unallocated' },
        { text: 'Partially Allocated', value: 'PartiallyAllocated' },
        { text: 'Allocated', value: 'Allocated' }
      ] : []
      this.selectedStatuses = allStatuses
      var values = allStatuses.map(function (o) { return o.value })
      this.$store.commit('setAllocationsFilter', { field: 'status', value: values })
    },

    handleTypeFilterChange: function (selectedOptions) {
      var values = selectedOptions.map(function (o) { return o.value })
      this.$store.commit('setAllocationsFilter', { field: 'type', value: values })
    },

    handleApplyFilters: function () {
      this.$store.commit('setError', null)
      this.$store.dispatch('applyFilters', this.customerId)
    },

    handleResetFilters: function () {
      var vm = this
      this.referenceFilter = ''
      this.dateRange = null
      this.amountFrom = null
      this.amountTo = null
      this.balanceFrom = null
      this.balanceTo = null
      this.selectedStatuses = [
        { text: 'Unallocated', value: 'Unallocated' },
        { text: 'Partially Allocated', value: 'PartiallyAllocated' }
      ]
      this.selectedTypes = []
      this.$store.dispatch('resetFilters', this.customerId)
        .then(function () {
          vm.syncFilterControlsFromQuery()
        })
    },

    goToPage: function (page) {
      this.$store.commit('setQuery', { page: page })
      this.$store.dispatch('fetchTransactions', this.customerId)
    },

    changePageSize: function (pageSize) {
      this.$store.commit('setQuery', { pageSize: pageSize, page: 1 })
      this.$store.dispatch('fetchTransactions', this.customerId)
    }
  }
}
</script>

<style scoped>
.manual-allocation-dashboard ::v-deep .multiselect__tag {
  background: #fff !important;
  border: 1px solid #ced4da;
  color: #000 !important;
}

.manual-allocation-dashboard ::v-deep .multiselect__tag-icon {
  background: transparent !important;
}

.manual-allocation-dashboard ::v-deep .multiselect__tag-icon::after {
  color: #6c757d !important;
}

.manual-allocation-dashboard ::v-deep .multiselect__tag-icon:hover {
  background: transparent !important;
}

.manual-allocation-dashboard ::v-deep .multiselect__tag-icon:hover::after {
  color: #000 !important;
}

.manual-allocation-dashboard .account-info-title {
  font-size: 0.875rem;
  font-weight: 600;
}

.manual-allocation-dashboard .account-info-value,
.manual-allocation-dashboard .account-contact-details {
  font-size: 1rem;
  font-weight: 400;
}

.manual-allocation-dashboard .summary-amount-highlight {
  font-size: 1rem;
}

.manual-allocation-dashboard ::v-deep .filter-label {
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
}

.manual-allocation-dashboard ::v-deep .form-control,
.manual-allocation-dashboard ::v-deep .multiselect,
.manual-allocation-dashboard ::v-deep .multiselect__tags,
.manual-allocation-dashboard ::v-deep .multiselect__input,
.manual-allocation-dashboard ::v-deep .multiselect__single,
.manual-allocation-dashboard ::v-deep .multiselect__placeholder,
.manual-allocation-dashboard ::v-deep .el-range-editor,
.manual-allocation-dashboard ::v-deep .el-range-input,
.manual-allocation-dashboard ::v-deep .el-range-separator {
  font-size: 16px !important;
}

.manual-allocation-dashboard ::v-deep .filter-panel .input-group > .form-control:not(:last-child),
.manual-allocation-dashboard ::v-deep .filter-panel .input-group > .form-control:last-child,
.manual-allocation-dashboard ::v-deep .filter-panel .input-group > .custom-select:not(:last-child),
.manual-allocation-dashboard ::v-deep .filter-panel .input-group > .custom-select:last-child {
  font-size: 16px !important;
}

.manual-allocation-dashboard ::v-deep .filter-panel .el-date-editor--daterange .el-range-input,
.manual-allocation-dashboard ::v-deep .filter-panel .el-date-editor--daterange .el-range-separator,
.manual-allocation-dashboard ::v-deep .filter-panel .el-date-editor--daterange .el-range__icon,
.manual-allocation-dashboard ::v-deep .filter-panel .el-date-editor--daterange .el-range__close-icon {
  line-height: calc(1.8125rem - 2px) !important;
}

.manual-allocation-dashboard ::v-deep .filter-panel .el-date-editor--daterange.el-input__inner {
  height: calc(1.8125rem + 2px) !important;
  min-height: calc(1.8125rem + 2px) !important;
}

.manual-allocation-dashboard ::v-deep .form-control::placeholder,
.manual-allocation-dashboard ::v-deep .multiselect__input::placeholder,
.manual-allocation-dashboard ::v-deep .el-range-input::placeholder {
  font-size: 16px !important;
}

.manual-allocation-dashboard .dashboard-filter-button {
  font-size: 0.9rem;
  min-width: 120px;
  min-height: 36px;
}

.manual-allocation-dashboard ::v-deep .dashboard-tabs .nav-tabs .nav-link {
  color: #495057;
  font-weight: 600;
}

.manual-allocation-dashboard ::v-deep .dashboard-tabs .nav-tabs .nav-link:not(.active) {
  background-color: #f8f9fa;
}

.manual-allocation-dashboard ::v-deep .dashboard-tabs .nav-tabs .nav-link.active {
  color: #212529;
  background-color: #fff;
  border-color: #dee2e6 #dee2e6 #fff;
}
</style>
